import { useState, useEffect, useCallback } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { Toast, ToastType } from '@/components/ui/Toast'
import { ListPageSkeleton } from '@/components/ui'
import { useTranslation } from 'react-i18next'
import {
  ShoppingCart,
  Check,
  X,
  Loader2,
  Search,
  Filter,
  History,
  Clock,
} from 'lucide-react'
import { apiService } from '../../services/api'
import { VehiclePublicationLogs } from './components/VehiclePublicationLogs'
import { MarketplaceIntegrationLogo } from './components/MarketplaceIntegrationLogo'

interface Vehicle {
  id: string
  brand: string
  model: string
  version?: string
  modelYear: number
  manufactureYear: number
  licensePlate: string
  salePrice: number
  status: string
  photo?: string | null
  publications: {
    id: string
    platform: string
    status: string
    externalUrl?: string
    publishedAt?: string
  }[]
}

interface Integration {
  id: string
  platform: string
  isActive: boolean
}

interface AutoPublishConfig {
  isActive: boolean
  schedules: string[]
}

export function PublicationsPage() {
  const { t } = useTranslation()
  const [vehicles, setVehicles] = useState<Vehicle[]>([])
  const [integrations, setIntegrations] = useState<Integration[]>([])
  const [selectedVehicles, setSelectedVehicles] = useState<Set<string>>(new Set())
  const [selectedIntegration, setSelectedIntegration] = useState<string>('')
  const [loading, setLoading] = useState(true)
  const [publishing, setPublishing] = useState(false)
  const [search, setSearch] = useState('')
  const [autoPublishConfig, setAutoPublishConfig] = useState<AutoPublishConfig>({
    isActive: false,
    schedules: [],
  })
  const [autoPublishSaving, setAutoPublishSaving] = useState(false)
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null)
  const [selectedVehicleForLogs, setSelectedVehicleForLogs] = useState<{
    id: string
    name: string
  } | null>(null)

  const loadData = useCallback(async () => {
    try {
      setLoading(true)
      const [vehiclesData, integrationsData, autoPublishData] = await Promise.all([
        apiService.get<Vehicle[]>('/integrations/publications/vehicles/available'),
        apiService.get<{ integrations: Integration[] }>('/integrations'),
        apiService.get<AutoPublishConfig>('/integrations/auto-publish/config'),
      ])

      setVehicles(vehiclesData)
      setIntegrations(integrationsData.integrations.filter((i: Integration) => i.isActive))
      setAutoPublishConfig({
        isActive: autoPublishData.isActive,
        schedules: autoPublishData.schedules ?? [],
      })
    } catch (err: any) {
      setToast({
        message: err.message || 'Erro ao carregar dados',
        type: 'error',
      })
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadData()
  }, [loadData])

  const handleToggleAutoPublish = async () => {
    const nextActive = !autoPublishConfig.isActive
    setAutoPublishSaving(true)
    try {
      const updated = await apiService.patch<AutoPublishConfig>(
        '/integrations/auto-publish/config',
        { isActive: nextActive }
      )
      setAutoPublishConfig({
        isActive: updated.isActive,
        schedules: updated.schedules ?? [],
      })
    } catch (err: any) {
      setToast({
        message: err.message || t('marketplace.publications.autoPublish.error'),
        type: 'error',
      })
    } finally {
      setAutoPublishSaving(false)
    }
  }

  const handleSelectAll = () => {
    if (selectedVehicles.size === vehicles.length) {
      setSelectedVehicles(new Set())
    } else {
      setSelectedVehicles(new Set(vehicles.map((v) => v.id)))
    }
  }

  const handleSelectVehicle = (vehicleId: string) => {
    const newSelected = new Set(selectedVehicles)
    if (newSelected.has(vehicleId)) {
      newSelected.delete(vehicleId)
    } else {
      newSelected.add(vehicleId)
    }
    setSelectedVehicles(newSelected)
  }

  const handlePublish = async () => {
    if (!selectedIntegration) {
      setToast({
        message: t('marketplace.publications.selectMarketplace'),
        type: 'error',
      })
      return
    }

    if (selectedVehicles.size === 0) {
      setToast({
        message: t('marketplace.publications.selectAtLeastOne'),
        type: 'error',
      })
      return
    }

    try {
      setPublishing(true)
      const result = await apiService.post<{
        total: number
        queued: number
        failed: number
      }>('/integrations/publications/batch', {
        vehicleIds: Array.from(selectedVehicles),
        integrationId: selectedIntegration,
      })

      setToast({
        message: t('marketplace.publications.success', { queued: result.queued, failed: result.failed }),
        type: result.failed > 0 ? 'warning' : 'success',
      })

      setSelectedVehicles(new Set())
      loadData()
    } catch (err: any) {
      setToast({
        message: err.message || t('marketplace.publications.error'),
        type: 'error',
      })
    } finally {
      setPublishing(false)
    }
  }

  const getPlatformIcon = (platform: string) => (
    <MarketplaceIntegrationLogo platform={platform} alt={platform} size="md" />
  )

  const getPlatformColor = (platform: string) => {
    switch (platform.toUpperCase()) {
      case 'META':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
      case 'MERCADO_LIVRE':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
      case 'OLX':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
    }
  }

  const filteredVehicles = vehicles.filter((v) =>
    search
      ? `${v.brand} ${v.model} ${v.licensePlate}`
          .toLowerCase()
          .includes(search.toLowerCase())
      : true
  )

  if (loading) {
    return <ListPageSkeleton variant="table" rows={10} />
  }

  return (
    <>
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}

      <div className="p-6 space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-semibold text-foreground">
            {t('marketplace.publications.title')}
          </h1>
          <p className="text-muted-foreground">
            {t('marketplace.publications.subtitle')}
          </p>
        </div>

        {/* Auto Publish Settings */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>{t('marketplace.publications.autoPublish.title')}</CardTitle>
                <CardDescription>
                  {t('marketplace.publications.autoPublish.description')}
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  {autoPublishConfig.isActive
                    ? t('marketplace.publications.autoPublish.enabled')
                    : t('marketplace.publications.autoPublish.disabled')}
                </span>
                <button
                  onClick={handleToggleAutoPublish}
                  disabled={autoPublishSaving}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 ${
                    autoPublishConfig.isActive ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      autoPublishConfig.isActive ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          </CardHeader>
          {autoPublishConfig.isActive && (
            <CardContent className="border-t border-border pt-4">
              <div>
                <h4 className="text-sm font-medium mb-3">
                  {t('marketplace.publications.autoPublish.scheduleInfo')}
                </h4>
                <p className="text-sm text-muted-foreground mb-3">
                  {t('marketplace.publications.autoPublish.scheduleDescription')}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {autoPublishConfig.schedules.map((time) => (
                    <div key={time} className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
                      <Clock className="w-4 h-4 text-primary" />
                      <span className="text-sm">{time}</span>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Filters and Actions */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search */}
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <input
                  type="text"
                  placeholder={t('marketplace.publications.searchVehicles')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-border rounded-md bg-background text-foreground"
                />
              </div>

              {/* Marketplace Select */}
              <select
                value={selectedIntegration}
                onChange={(e) => setSelectedIntegration(e.target.value)}
                className="px-4 py-2 border border-border rounded-md bg-background text-foreground min-w-[200px]"
              >
                <option value="">{t('marketplace.publications.selectMarketplace')}</option>
                {integrations.map((integration) => (
                  <option key={integration.id} value={integration.id}>
                    {integration.platform}
                  </option>
                ))}
              </select>

              {/* Publish Button */}
              <Button
                onClick={handlePublish}
                disabled={selectedVehicles.size === 0 || !selectedIntegration || publishing}
                className="min-w-[150px]"
              >
                {publishing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('marketplace.publications.publishing')}
                  </>
                ) : (
                  <>
                    <ShoppingCart className="w-4 h-4 mr-2" />
                    {t('marketplace.publications.publish')} ({selectedVehicles.size})
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Vehicles Table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>
                {t('marketplace.publications.availableVehicles')} ({filteredVehicles.length})
              </CardTitle>
              <Button variant="outline" size="sm" onClick={handleSelectAll}>
                {selectedVehicles.size === vehicles.length
                  ? t('marketplace.publications.deselectAll')
                  : t('marketplace.publications.selectAll')}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left p-3 w-12"></th>
                    <th className="text-left p-3">{t('marketplace.publications.vehicle')}</th>
                    <th className="text-left p-3">{t('marketplace.publications.year')}</th>
                    <th className="text-left p-3">{t('marketplace.publications.price')}</th>
                    <th className="text-left p-3">{t('marketplace.publications.status')}</th>
                    <th className="text-left p-3">{t('marketplace.publications.publishedOn')}</th>
                    <th className="text-left p-3 w-32">{t('marketplace.publications.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredVehicles.map((vehicle) => (
                    <tr
                      key={vehicle.id}
                      className="border-b border-border hover:bg-muted/50 cursor-pointer"
                      onClick={() => handleSelectVehicle(vehicle.id)}
                    >
                      <td className="p-3">
                        <input
                          type="checkbox"
                          checked={selectedVehicles.has(vehicle.id)}
                          onChange={() => handleSelectVehicle(vehicle.id)}
                          onClick={(e) => e.stopPropagation()}
                          className="w-4 h-4 rounded border-border"
                        />
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-3">
                          {vehicle.photo && (
                            <img
                              src={vehicle.photo}
                              alt={`${vehicle.brand} ${vehicle.model}`}
                              className="w-12 h-12 rounded object-cover"
                            />
                          )}
                          <div>
                            <div className="font-medium">
                              {vehicle.brand} {vehicle.model}
                            </div>
                            {vehicle.version && (
                              <div className="text-sm text-muted-foreground">
                                {vehicle.version}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="p-3">{vehicle.modelYear}</td>
                      <td className="p-3">
                        R$ {vehicle.salePrice?.toLocaleString('pt-BR')}
                      </td>
                      <td className="p-3">
                        <Badge variant="primary">{vehicle.status}</Badge>
                      </td>
                      <td className="p-3">
                        <div className="flex gap-1 flex-wrap">
                          {vehicle.publications.map((pub) => (
                            <Badge
                              key={pub.id}
                              className={`${getPlatformColor(pub.platform)} inline-flex flex-col items-center gap-1 py-2 px-2`}
                            >
                              {getPlatformIcon(pub.platform)}
                              <span className="max-w-[10rem] text-center text-[0.65rem] font-semibold leading-tight">
                                {pub.platform}
                              </span>
                            </Badge>
                          ))}
                          {vehicle.publications.length === 0 && (
                            <span className="text-sm text-muted-foreground">
                              {t('marketplace.publications.notPublished')}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-3" onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            setSelectedVehicleForLogs({
                              id: vehicle.id,
                              name: `${vehicle.brand} ${vehicle.model}`,
                            })
                          }
                        >
                          <History className="w-4 h-4 mr-1" />
                          {t('marketplace.publications.history')}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Vehicle Logs Dialog */}
        {selectedVehicleForLogs && (
          <VehiclePublicationLogs
            isOpen={!!selectedVehicleForLogs}
            onClose={() => setSelectedVehicleForLogs(null)}
            vehicleId={selectedVehicleForLogs.id}
            vehicleName={selectedVehicleForLogs.name}
          />
        )}
      </div>
    </>
  )
}
